import type { PropertyConfigCode, SmartNumberDescription, SmartNumberMapping } from "@/features/sub-families/types";
import type { SmartNumberFieldChange } from "@/features/diagnostics/types";
import { SmartNumberDescriptor, type Nullable, type SmartNumberDescriptorKey } from "@/types";

const EMPTY_CONFIG_CODE = "" as PropertyConfigCode;

export type SmartNumberDisplayRow = {
    index: number;
    descriptor: SmartNumberDescriptorKey;
    configCode: PropertyConfigCode;
};

export function calculateSmartNumber(mapping: SmartNumberMapping, description: SmartNumberDescription): string {
    return description.reduce<string>((acc, { CharacteristicName }) => {
        const value = mapping[CharacteristicName];
        if (value) acc += value;

        return acc;
    }, "");
}

export function getDescriptionDisplayRows(
    description: SmartNumberDescription,
    mapping: SmartNumberMapping,
): SmartNumberDisplayRow[] {
    return description.map(({ CharacteristicName }, index) => ({
        index,
        descriptor: CharacteristicName,
        configCode: mapping[CharacteristicName] ?? EMPTY_CONFIG_CODE,
    }));
}

export function getMappingDescriptors(description: SmartNumberDescription): SmartNumberDescriptorKey[] {
    return description
        .map(({ CharacteristicName }) => CharacteristicName)
        .filter((descriptor) => descriptor !== SmartNumberDescriptor.Separator);
}

export function diffSmartNumberMapping(
    prev: Nullable<SmartNumberMapping>,
    next: SmartNumberMapping,
    description: SmartNumberDescription,
): SmartNumberFieldChange[] {
    return getMappingDescriptors(description)
        .filter((descriptor) => (prev?.[descriptor] ?? EMPTY_CONFIG_CODE) !== (next[descriptor] ?? EMPTY_CONFIG_CODE))
        .map((descriptor) => ({
            descriptor,
            from: prev?.[descriptor] ?? EMPTY_CONFIG_CODE,
            to: next[descriptor] ?? EMPTY_CONFIG_CODE,
        }));
}
